
import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CreateDistributionCenterPriceInput, DistributionCenterPrice } from '@/types/database/distribution-center-prices';
import { useCreateDistributionCenterPrice, useUpdateDistributionCenterPrice } from '@/hooks/use-distribution-center-prices';

const formSchema = z.object({
  product_name: z.string().min(1, 'Product name is required'),
  price: z.number().min(0, 'Price must be non-negative'),
  location: z.string().min(1, 'Location is required'),
  product_remark: z.string().optional(),
  date: z.string().min(1, 'Date is required'),
});

interface DistributionCenterPriceFormProps {
  open: boolean;
  onClose: () => void;
  price?: DistributionCenterPrice;
}

const DistributionCenterPriceForm: React.FC<DistributionCenterPriceFormProps> = ({ 
  open, 
  onClose, 
  price 
}) => {
  const createMutation = useCreateDistributionCenterPrice();
  const updateMutation = useUpdateDistributionCenterPrice();
  const isEditing = !!price;

  const form = useForm<CreateDistributionCenterPriceInput>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      product_name: price?.product_name || '',
      price: price?.price || 0,
      location: price?.location || '',
      product_remark: price?.product_remark || '',
      date: price?.date || new Date().toISOString().split('T')[0],
    },
  });

  const onSubmit = async (data: CreateDistributionCenterPriceInput) => {
    try {
      if (isEditing && price) {
        await updateMutation.mutateAsync({ id: price.id, price: data });
      } else {
        await createMutation.mutateAsync(data);
      }
      form.reset();
      onClose();
    } catch (error) {
      console.error('Form submission error:', error);
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Distribution Center Price' : 'Add Distribution Center Price'}
          </DialogTitle>
        </DialogHeader>
        
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="product_name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Product Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter product name" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="price"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Price (ETB)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        placeholder="0.00"
                        step="0.01"
                        {...field}
                        onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Date</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="location"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Location</FormLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select distribution center" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="Distribution center 1 Gerji">Distribution center 1 Gerji</SelectItem>
                      <SelectItem value="Distribution center 2 Garment">Distribution center 2 Garment</SelectItem>
                      <SelectItem value="Distribution center 3 02">Distribution center 3 02</SelectItem>
                      <SelectItem value="Distribution center 4 6Killo">Distribution center 4 6Killo</SelectItem>
                      <SelectItem value="Distribution center 5 Ayat Yafo">Distribution center 5 Ayat Yafo</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="product_remark"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Product Remark</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Enter any remarks about the product" 
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {isEditing ? 'Update' : 'Add'} Price
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default DistributionCenterPriceForm;
